import { useMemo, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router';
import {
  DONE_WINDOW_DAYS,
  endOfDayInZone,
  startOfDayInZone,
  TASK_PAGE_SIZE,
  type Task,
} from '@cleartask/shared';
import { useTimeZone } from '../session.js';
import { QuickAdd } from '../features/tasks/QuickAdd.js';
import { TaskCard } from '../features/tasks/TaskCard.js';
import { SnoozeMenu, type SnoozePayload } from '../features/tasks/SnoozeMenu.js';
import { DetalheNaUrl, useDetalheNaUrl } from '../features/tasks/DetalheNaUrl.js';
import { usePainelFixo } from '../features/tasks/usePainelFixo.js';
import { useLists } from '../features/lists/useLists.js';
import { useTags } from '../features/tags/useTags.js';
import type { TaskView } from '../lib/api.js';
import {
  useCompleteTask,
  useDeleteTask,
  useReopenTask,
  useSnoozeTask,
  useTaskList,
} from '../features/tasks/useTasks.js';

type Visao = 'today' | 'upcoming' | 'all';

/**
 * As tres visoes da aba Tarefas.
 *
 * "Todas" existe porque a tela Hoje, sozinha, escondia trabalho: uma tarefa com
 * prazo para semana que vem so aparecia no calendario, e o estado vazio de Hoje
 * chegava a sugerir "puxe algo de Proximos 7 dias" — uma tela que nao existia.
 *
 * **"Todas" e tudo que nao esta na lixeira**, concluidas inclusive. Ate aqui
 * nenhuma das tres passava `includeDone`, e a view `list` do servidor aplica
 * `status = 'open'` quando ele falta — entao "Todas" nao era todas, e o unico
 * lugar do app onde uma tarefa concluida aparecia era a paleta. Quem quisesse
 * simplesmente olhar o que fez nao tinha onde.
 *
 * Hoje e 7 dias continuam sem as concluidas: as duas sao recortes de trabalho
 * PENDENTE, e o servidor as filtra por `status` de qualquer forma. A visao e o
 * proprio controle — um botao "mostrar concluidas" por cima dela seria um
 * segundo jeito de dizer a mesma coisa.
 */
interface Definicao {
  id: Visao;
  label: string;
  view: TaskView;
  includeDone: boolean;
  /**
   * Esconde as subtarefas, deixando so o primeiro nivel.
   *
   * **So em "Todas"**, e a assimetria e o ponto. Em "Todas" o pai TAMBEM esta na
   * lista, entao a filha aparecia ao lado dele sem nada dizendo que uma e parte
   * da outra — duas linhas para o mesmo trabalho, que e como o defeito se
   * manifestou no primeiro uso real. Ali a decomposicao ja esta visivel no
   * detalhe do pai, e o contador de subtarefas diz que ela existe.
   *
   * "Hoje" e "7 dias" continuam trazendo: sao recortes por DATA, e uma
   * subtarefa com prazo para hoje vence hoje. Esconde-la faria sumir trabalho
   * datado da unica tela que responde "o que e para hoje" — que e exatamente a
   * queixa que originou a §8g, agora do outro lado.
   */
  apenasPrimeiroNivel: boolean;
}

const VISOES: Definicao[] = [
  { id: 'today', label: 'Hoje', view: 'today', includeDone: false, apenasPrimeiroNivel: false },
  {
    id: 'upcoming',
    label: '7 dias',
    view: 'upcoming',
    includeDone: false,
    apenasPrimeiroNivel: false,
  },
  { id: 'all', label: 'Todas', view: 'list', includeDone: true, apenasPrimeiroNivel: true },
];

/**
 * Aba Tarefas (spec 9.1 e 9.6).
 *
 * A consulta vem filtrada do servidor; aqui so se agrupa. A fronteira usa o
 * inicio do dia civil no fuso do usuario — comparar com `agora - 24h`
 * classificaria como vencida uma tarefa das 23h de ontem e como de hoje uma das
 * 2h da manha.
 *
 * O filtro por lista vem da URL (`?lista=<id>`), e nao de estado local, para o
 * link da barra lateral funcionar e o endereco poder ser compartilhado. **O
 * detalhe aberto segue a mesma regra** (`/tarefas/:id`), pelo mesmo motivo.
 */
export function Tasks() {
  const timeZone = useTimeZone();
  const [params, setParams] = useSearchParams();

  // Abre em "Todas". "Hoje" e o recorte mais estreito das tres e escondia duas
  // coisas ao mesmo tempo: o que vence a partir de amanha e o que ja foi feito.
  // Abrir no recorte e util para executar; abrir no todo e util para SABER o
  // que existe — e essa foi a queixa que trouxe a §8g. As outras duas visoes
  // continuam a um clique.
  const [visao, setVisao] = useState<Visao>('all');

  /**
   * Quantas tarefas pedir.
   *
   * Comeca no teto do servidor e sobe de um bloco por vez, quando a pessoa
   * pede. Nao ha `offset`: a ordem e por prazo e muda quando uma tarefa e
   * adiada, entao pedir "as proximas 300" por deslocamento pularia ou
   * repetiria linhas. Pedir de novo, mais fundo, sempre devolve um recorte
   * coerente.
   */
  const [limite, setLimite] = useState(TASK_PAGE_SIZE);

  function trocarVisao(proxima: Visao) {
    setVisao(proxima);
    // O teto volta ao inicial: cada visao e um recorte diferente, e carregar
    // 900 linhas em "Hoje" porque "Todas" precisou delas nao ajuda ninguem.
    setLimite(TASK_PAGE_SIZE);
  }

  const listaFiltrada = params.get('lista');
  const { data: listasData } = useLists();
  const lista = (listasData?.lists ?? []).find((l) => l.id === listaFiltrada) ?? null;

  // O filtro por tag vem da URL pelo mesmo motivo que o de lista: o link da
  // barra lateral precisa funcionar e o endereco precisa poder ser
  // compartilhado. Os dois se acumulam — "tarefas da lista X marcadas com #y" e
  // uma pergunta legitima, e cada um sozinho continua valendo.
  const tagFiltrada = params.get('tag');
  const { data: tagsData } = useTags();
  const tag = (tagsData?.tags ?? []).find((t) => t.id === tagFiltrada) ?? null;

  // Os filtros viajam junto com o detalhe: abrir uma tarefa a partir de "#casa"
  // e voltar dali nao pode devolver a lista inteira, que e outra tela.
  const sufixoDeFiltro = params.toString() === '' ? '' : `?${params.toString()}`;

  /**
   * A tarefa do endereco.
   *
   * `useTask`, por dentro, responde do cache das listas quando o card veio da
   * propria tela e so vai a rede no caso que justifica ter endereco: link
   * colado noutro aparelho, ou F5 em cima do detalhe aberto.
   */
  const { detalhe, abrir, fechar, abrirPorId } = useDetalheNaUrl('/tarefas', sufixoDeFiltro);
  const painelFixo = usePainelFixo();
  const selecionadaId = detalhe.tipo === 'aberto' ? detalhe.task.id : null;

  const visaoAtual = VISOES.find((v) => v.id === visao);

  const { data, isPending, isError, error } = useTaskList({
    view: visaoAtual?.view ?? 'today',
    includeDone: visaoAtual?.includeDone ?? false,
    // A string 'null' e o valor que o servidor entende como "sem pai": um
    // parametro ausente ja significa "sem filtro" (§8r).
    ...(visaoAtual?.apenasPrimeiroNivel ? { parentId: 'null' as const } : {}),
    ...(listaFiltrada ? { listId: listaFiltrada } : {}),
    ...(tagFiltrada ? { tagId: tagFiltrada } : {}),
    limit: limite,
    // O recorte so vale onde ha concluidas para recortar. "Hoje" e "7 dias"
    // nem as pedem, e mandar o parametro ali sugeriria um filtro que nao
    // existe naquelas telas.
    ...(visaoAtual?.includeDone ? { doneDays: DONE_WINDOW_DAYS } : {}),
  });

  const completeTask = useCompleteTask();
  const reopenTask = useReopenTask();
  const snoozeTask = useSnoozeTask();
  const deleteTask = useDeleteTask();

  // O relogio de referencia e o do SERVIDOR, devolvido junto com a consulta. Um
  // dispositivo com a hora errada mostraria um agrupamento incoerente com o
  // filtro que o proprio servidor acabou de aplicar.
  const now = data?.now ?? Date.now();

  const grupos = useMemo(() => {
    const tarefas = data?.tasks ?? [];
    const inicioDeHoje = startOfDayInZone(timeZone, now);
    const fimDeHoje = endOfDayInZone(timeZone, now);

    // A separacao por STATUS vem antes da separacao por prazo. Agrupar tudo por
    // data primeiro colocaria uma tarefa feita ontem, com prazo para semana que
    // vem, dentro de "proximas" — ao lado das que ainda pedem trabalho, e com o
    // contador do cabecalho afirmando que ha mais coisa por fazer do que ha.
    const abertas = tarefas.filter((t) => t.status !== 'done');

    return {
      vencidas: abertas.filter((t) => t.dueAt !== null && t.dueAt < inicioDeHoje),
      deHoje: abertas.filter(
        (t) => t.dueAt !== null && t.dueAt >= inicioDeHoje && t.dueAt <= fimDeHoje,
      ),
      proximas: abertas.filter((t) => t.dueAt !== null && t.dueAt > fimDeHoje),
      // Grupo proprio, e nao misturado em "hoje": uma tarefa sem prazo nao
      // vence hoje. Antes desta correcao ela nao aparecia em lugar nenhum.
      semPrazo: abertas.filter((t) => t.dueAt === null),
      // Ordenadas pela CONCLUSAO, e nao pelo prazo como o resto: o que importa
      // num registro do que foi feito e o que saiu por ultimo. A ordem do
      // servidor (`ORDER BY due_at`) enterraria a tarefa recem-marcada no meio
      // da lista, e o gesto mais comum aqui e conferir o que acabou de sair.
      // `?? 0` cobre a janela entre o patch otimista e a resposta.
      executados: tarefas
        .filter((t) => t.status === 'done')
        .sort((a, b) => (b.completedAt ?? 0) - (a.completedAt ?? 0)),
    };
  }, [data?.tasks, now, timeZone]);

  function adiarTodas(tarefas: Task[], payload: SnoozePayload) {
    for (const task of tarefas) snoozeTask.mutate({ task, payload });
  }

  /*
    A tarefa aberta que SAI da lista leva o painel junto. Sem isto o detalhe
    perde a linha do cache, `useTask` vai a rede atras dela e o painel fica
    preso em "Abrindo a tarefa…" sem rede — ou, com rede, reabre como "na
    lixeira" uma tarefa que a pessoa acabou de excluir de proposito. Concluir
    so fecha onde a visao nao mostra concluidas: em "Todas" a tarefa fica,
    riscada, e o painel continua util.
  */
  const fecharSeAberta = (task: Task) => {
    if (task.id === selecionadaId) fechar();
  };

  const acoesDoCard: AcoesDoCard = {
    onComplete: (task) => {
      completeTask.mutate(task);
      if (!visaoAtual?.includeDone) fecharSeAberta(task);
    },
    onReopen: (task) => reopenTask.mutate(task),
    onSnooze: (task, payload) => snoozeTask.mutate({ task, payload }),
    onDelete: (task) => {
      deleteTask.mutate(task);
      fecharSeAberta(task);
    },
    // Abrir EMPILHA, fechar SUBSTITUI. Assim o "voltar" do aparelho fecha o
    // detalhe (o gesto que todo mundo tenta antes de procurar o ✕) sem que
    // fechar pelo ✕ deixe para tras um passo que reabriria o painel.
    onOpen: abrir,
  };

  const vazio =
    grupos.vencidas.length === 0 &&
    grupos.deHoje.length === 0 &&
    grupos.proximas.length === 0 &&
    grupos.semPrazo.length === 0 &&
    grupos.executados.length === 0;

  const colunaDaLista = (
    <div className="mx-auto flex max-w-2xl flex-col gap-4 p-4">
      <header className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="font-display text-lg font-semibold">
          {lista ? lista.name : tag ? `#${tag.name}` : 'Tarefas'}
        </h1>

        <div className="flex gap-1" role="tablist" aria-label="Visão">
          {VISOES.map((item) => (
            <button
              key={item.id}
              type="button"
              role="tab"
              aria-selected={item.id === visao}
              onClick={() => trocarVisao(item.id)}
              className={[
                'min-h-touch rounded-sm px-3 text-sm transition-colors duration-150',
                item.id === visao
                  ? 'bg-brand-blue-soft font-medium text-brand-blue'
                  : 'text-text-mut hover:bg-surface-2',
              ].join(' ')}
            >
              {item.label}
            </button>
          ))}
        </div>
      </header>

      {listaFiltrada !== null || tagFiltrada !== null ? (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-text-mut">
            {listaFiltrada !== null && tagFiltrada !== null
              ? 'Filtrando por lista e tag'
              : listaFiltrada !== null
                ? 'Filtrando por lista'
                : 'Filtrando por tag'}
          </span>
          <button
            type="button"
            onClick={() => {
              const proximos = new URLSearchParams(params);
              proximos.delete('lista');
              proximos.delete('tag');
              setParams(proximos);
            }}
            className="min-h-touch rounded-sm bg-surface-2 px-3 font-medium"
          >
            Limpar filtro
          </button>
        </div>
      ) : null}

      <QuickAdd
        // Sem prazo padrao fora da visao Hoje: em "Todas", carimbar hoje numa
        // tarefa que a pessoa nao datou seria inventar uma decisao dela.
        defaultDueAt={visao === 'today' ? endOfDayInZone(timeZone, now) : null}
        defaultListId={listaFiltrada}
        // Criar uma tarefa sob o filtro de uma tag ja a marca com ela; sem isso
        // a tarefa nasceria fora do filtro e sumiria no mesmo instante.
        defaultTagIds={tagFiltrada ? [tagFiltrada] : []}
      />

      {isPending ? <Skeleton /> : null}

      {isError ? (
        <p className="rounded bg-surface p-4 text-sm text-overdue" role="alert">
          {error instanceof Error ? error.message : 'Não foi possível carregar as tarefas.'}
        </p>
      ) : null}

      {!isPending && !isError ? (
        <>
          {grupos.vencidas.length > 0 ? (
            <Grupo
              titulo="vencido"
              tarefas={grupos.vencidas}
              now={now}
              acoes={acoesDoCard}
              selecionadaId={selecionadaId}
              acaoEmLote={
                <SnoozeMenu
                  label="Adiar"
                  onSelect={(payload) => adiarTodas(grupos.vencidas, payload)}
                />
              }
            />
          ) : null}

          {grupos.deHoje.length > 0 ? (
            <Grupo
              titulo="hoje"
              tarefas={grupos.deHoje}
              now={now}
              acoes={acoesDoCard}
              selecionadaId={selecionadaId}
            />
          ) : null}

          {grupos.proximas.length > 0 ? (
            <Grupo
              titulo="próximas"
              tarefas={grupos.proximas}
              now={now}
              acoes={acoesDoCard}
              selecionadaId={selecionadaId}
            />
          ) : null}

          {grupos.semPrazo.length > 0 ? (
            <Grupo
              titulo="sem prazo"
              tarefas={grupos.semPrazo}
              now={now}
              acoes={acoesDoCard}
              selecionadaId={selecionadaId}
            />
          ) : null}

          {/*
            Por ultimo, sempre: o que ja foi feito nao disputa atencao com o que
            falta. So aparece na visao "Todas" — as outras duas nem pedem as
            concluidas ao servidor.
          */}
          {grupos.executados.length > 0 ? (
            <Grupo
              titulo="executados"
              tarefas={grupos.executados}
              now={now}
              acoes={acoesDoCard}
              selecionadaId={selecionadaId}
              // O recorte precisa estar escrito onde ele age. Sem esta linha,
              // uma tarefa concluida em maio simplesmente nao estaria aqui — e
              // "sumiu" e a leitura natural de uma lista que nao explica o
              // proprio limite.
              nota={`últimos ${DONE_WINDOW_DAYS} dias`}
            />
          ) : null}

          {/*
            O teto cortou a resposta. A alternativa seria rolagem infinita, que
            esconde o proprio corte: a pessoa nao saberia se chegou ao fim ou se
            a lista ainda esta chegando.
          */}
          {data?.hasMore ? (
            <div className="flex flex-col items-center gap-1 pb-2">
              <button
                type="button"
                onClick={() => setLimite((atual) => atual + TASK_PAGE_SIZE)}
                className="min-h-touch rounded-sm bg-surface px-4 text-sm font-medium shadow-card"
              >
                Mostrar mais
              </button>
              <p className="text-xs text-text-mut">
                Mostrando as primeiras {limite}. Buscar por texto (⌘K) alcança o resto.
              </p>
            </div>
          ) : null}

          {vazio ? (
            <Vazio
              visao={visao}
              temFiltro={listaFiltrada !== null || tagFiltrada !== null}
            />
          ) : null}
        </>
      ) : null}

    </div>
  );

  // Tela larga: lista a esquerda e detalhe encaixado a direita, como no
  // TickTick. Sobreposto, o painel cobria justamente a lista de onde a
  // proxima tarefa seria escolhida. A mesma arvore nas duas larguras, so com
  // classes diferentes: girar o tablet nao remonta a lista (nem perde o texto
  // digitado no campo rapido). A gaveta e `fixed`, entao o lugar dela no DOM
  // nao muda onde ela aparece.
  return (
    <div className={painelFixo ? 'flex min-h-dvh' : undefined}>
      <div className={painelFixo ? 'min-w-0 flex-1' : undefined}>{colunaDaLista}</div>
      <DetalheNaUrl
        detalhe={detalhe}
        fechar={fechar}
        abrirPorId={abrirPorId}
        fixo={painelFixo}
      />
    </div>
  );
}

interface AcoesDoCard {
  onComplete: (task: Task) => void;
  onReopen: (task: Task) => void;
  onSnooze: (task: Task, payload: SnoozePayload) => void;
  onDelete: (task: Task) => void;
  onOpen: (task: Task) => void;
}

interface GrupoProps {
  titulo: string;
  tarefas: Task[];
  now: number;
  acoes: AcoesDoCard;
  /** Menu que age sobre o grupo inteiro — hoje so o "Adiar" do vencido. */
  acaoEmLote?: ReactNode;
  /** Recorte aplicado ao grupo, quando ele nao mostra tudo que existe. */
  nota?: string;
  /** A tarefa aberta no detalhe, marcada na lista. */
  selecionadaId: string | null;
}

function Grupo({ titulo, tarefas, now, acoes, acaoEmLote, nota, selecionadaId }: GrupoProps) {
  const [aberto, setAberto] = useState(true);

  return (
    <section className="overflow-hidden rounded bg-surface shadow-card">
      <header className="flex items-center gap-2 border-b border-border px-3 py-2">
        <button
          type="button"
          onClick={() => setAberto((valor) => !valor)}
          aria-expanded={aberto}
          className="min-h-touch flex-1 text-left text-sm font-medium text-text-mut"
        >
          {titulo} <span className="tabular-nums">{tarefas.length}</span>
          {nota ? <span className="font-normal"> · {nota}</span> : null}
        </button>

        {acaoEmLote}
      </header>

      {aberto ? (
        <ul>
          {tarefas.map((task) => (
            <TaskCard
              key={task.id}
              task={task}
              now={now}
              selecionada={task.id === selecionadaId}
              {...acoes}
            />
          ))}
        </ul>
      ) : null}
    </section>
  );
}

/**
 * Estado vazio instrutivo, nao decorativo (spec 9.5): diz o que aconteceu e
 * qual e o proximo passo.
 *
 * A copy muda com a visao. A versao anterior sugeria "puxe algo de Proximos 7
 * dias" em qualquer situacao — apontando para uma tela que nao existia.
 */
function Vazio({ visao, temFiltro }: { visao: Visao; temFiltro: boolean }) {
  if (temFiltro) {
    return (
      <div className="rounded bg-surface p-6 text-center shadow-card">
        <p className="font-medium">Nada neste filtro.</p>
        <p className="mt-1 text-sm text-text-mut">
          Adicione uma tarefa acima — ela já nasce com o filtro aplicado.
        </p>
      </div>
    );
  }

  const copy: Record<Visao, { titulo: string; passo: string }> = {
    today: {
      titulo: 'Nada para hoje.',
      passo: 'Adicione uma tarefa acima ou veja “7 dias”.',
    },
    upcoming: {
      titulo: 'Nada nos próximos 7 dias.',
      passo: 'O que não tem prazo aparece em “Todas”.',
    },
    // "Todas" mostra ate as concluidas, entao vazia aqui significa vazia
    // mesmo — nao ha outra visao para onde mandar a pessoa olhar. O que resta
    // fora dela e a lixeira, e e isso que a copy diz.
    all: {
      titulo: 'Nenhuma tarefa.',
      passo: 'Adicione a primeira acima. O que foi excluído fica em Mais → Lixeira.',
    },
  };

  return (
    <div className="rounded bg-surface p-6 text-center shadow-card">
      <p className="font-medium">{copy[visao].titulo}</p>
      <p className="mt-1 text-sm text-text-mut">{copy[visao].passo}</p>
    </div>
  );
}

/** Skeleton em vez de tela em branco (spec 10.4). */
function Skeleton() {
  return (
    <div className="flex flex-col gap-2" aria-hidden="true">
      {[0, 1, 2].map((i) => (
        <div key={i} className="h-12 animate-pulse rounded bg-surface-2" />
      ))}
    </div>
  );
}
